import React, { useRef, useEffect, useState, useCallback } from 'react';

const DrawingCanvas = ({
  drawingMode,
  brushSize = 2,
  brushColor = '#000000',
  brushOpacity = 1,
  width,
  height,
  zoom = 1,
  onStrokeComplete,
}) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [strokePoints, setStrokePoints] = useState([]);

  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.globalCompositeOperation = drawingMode === 'eraser' ? 'destination-out' : 'source-over';
  }, [drawingMode]);

  // Get tool-specific settings
  const getToolSettings = () => {
    switch (drawingMode) {
      case 'pen':
        return {
          strokeWidth: brushSize,
          opacity: brushOpacity,
        };
      case 'highlighter':
        return {
          strokeWidth: brushSize * 3,
          opacity: 0.3,
        };
      case 'marker':
        return {
          strokeWidth: brushSize * 2,
          opacity: brushOpacity,
        };
      case 'eraser':
        return {
          strokeWidth: brushSize * 2,
          opacity: 1,
        };
      default:
        return {
          strokeWidth: brushSize,
          opacity: brushOpacity,
        };
    }
  };

  // Convert screen coordinates to canvas coordinates
  const getCanvasPoint = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    // Use CSS pixel coordinates; context is scaled for DPR separately for sharpness
    return {
      x: (e.clientX - rect.left),
      y: (e.clientY - rect.top),
    };
  };

  // Handle mouse down
  const handleMouseDown = (e) => {
    if (!drawingMode || !canvasRef.current) return;

    const point = getCanvasPoint(e);
    if (!point) return;

    setIsDrawing(true);
    setStrokePoints([point]);

    const ctx = canvasRef.current.getContext('2d');
    const settings = getToolSettings();

    ctx.strokeStyle = drawingMode === 'eraser' ? 'rgba(255,255,255,1)' : brushColor;
    ctx.lineWidth = settings.strokeWidth;
    ctx.globalAlpha = settings.opacity;
    ctx.globalCompositeOperation = drawingMode === 'eraser' ? 'destination-out' : 'source-over';

    ctx.beginPath();
    ctx.moveTo(point.x, point.y);

  };

  // Handle mouse move
  const handleMouseMove = (e) => {
    if (!isDrawing || !canvasRef.current) return;

    const point = getCanvasPoint(e);
    if (!point) return;

    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(point.x, point.y);
    ctx.stroke();

    setStrokePoints(prev => [...prev, point]);
  };

  // Handle mouse up
  const handleMouseUp = (e) => {
    if (!isDrawing) return;

    setIsDrawing(false);
    // Finish path
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.closePath();

    // Report stroke to parent as vector points (in canvas coordinates)
    if (typeof onStrokeComplete === 'function' && strokePoints.length > 1) {
      onStrokeComplete({
        points: strokePoints.map(p => ({ x: p.x, y: p.y })),
        tool: drawingMode,
        brushSize,
        brushColor,
        brushOpacity,
        width: canvas.width,
        height: canvas.height,
      });
    }

    // Clear preview overlay
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setStrokePoints([]);
  };

  // Clear canvas
  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  }, []);

  // Expose methods to parent
  useEffect(() => {
    if (canvasRef.current) {
      canvasRef.current.clearCanvas = clearCanvas;
    }
  }, [clearCanvas]);

  // Handle drawing mode changes
  useEffect(() => {
    if (!drawingMode) {
      setIsDrawing(false);
      setStrokePoints([]);
    }
  }, [drawingMode]);

  // Update canvas size when dimensions change
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.floor(width * dpr));
    canvas.height = Math.max(1, Math.floor(height * dpr));
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const ctx = canvas.getContext('2d');
    // Reset transform before scaling
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, width, height);
  }, [width, height]);

  if (!drawingMode) return null;

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 pointer-events-auto"
      style={{
        width: `${width}px`,
        height: `${height}px`,
        transform: `scale(${zoom})`,
        transformOrigin: 'top left',
        cursor: drawingMode === 'eraser' ? 'crosshair' : 'crosshair',
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    />
  );
};

export default DrawingCanvas;
